// Centralized portfolio content. Edit freely.
//
// PHASE 2 NOTE: The `blog` array is no longer authored here.
// Blog posts live in /content/posts/<slug>.md as markdown files with YAML
// frontmatter. The build script (scripts/build-posts-index.js) compiles them
// into /src/data/postsIndex.js before `yarn start` and `yarn build`.
// To add or edit a blog post, touch the .md files (or use /admin in Phase 3).
import { posts as blogPosts } from "./postsIndex";

export const profile = {
  name: "Malicke Case",
  shortName: "Malicke",
  initials: "MC",
  role: "Engineering Student & Builder",
  location: "Kingston, Jamaica",
  email: "malickecase28@gmail.com",
  domain: "malickecase.com",
  tagline: "Electrical and computer engineering student building real things at the intersection of finance, hardware, and software.",
  longBio:
    "I was born in 2001 and grew up in a small rural community in the deep corners of Brown\u2019s Town, Jamaica. From an early age I was fascinated by creating things. Sometimes that meant drawing and visual art. Other times it meant music, science experiments, taking things apart, or figuring out how something worked. Over the years that curiosity grew into a passion for engineering, technology, and problem solving. Today I study Electrical and Computer Engineering at the University of Technology, Jamaica. I enjoy building systems, testing ideas, and turning concepts into things that actually work. One project I am particularly proud of is IMMBRAIN, a trading and risk management system that I started from scratch. Building it has taught me a great deal about software, markets, data, risk, and the discipline required to bring a complex idea to life. What drives me most is the belief that no matter where you come from, you can build meaningful things if you stay curious and keep learning.",
  socials: [
    { label: "X", href: "https://x.com/Malicke_msb1000", short: "X" },
    { label: "LinkedIn", href: "https://linkedin.com/in/malicke-case-41a788177", short: "in" },
    { label: "Email", href: "mailto:malickecase28@gmail.com", short: "@" }
  ]
};

export const greetings = [
  { lang: "English",  word: "Hello" },
  { lang: "Spanish",  word: "Hola" },
  { lang: "French",   word: "Bonjour" },
  { lang: "Mandarin", word: "\u4f60\u597d" },
  { lang: "Hindi",    word: "\u0928\u092e\u0938\u094d\u0924\u0947" }
];

export const nowList = [
  "Building IMMBRAIN, a trading and risk-modeling engine",
  "Finishing my engineering degree at UTech Jamaica",
  "Field-testing the solar IoT drip irrigation system",
  "Learning more about portfolio management and market structure"
];

export const principles = [
  "Build it once, build it right.",
  "Ship the system, not the demo.",
  "Understand the problem before writing a line.",
  "Real work beats impressive presentations."
];

export const projects = [
  {
    id: "immbrain",
    preview: "/assets/projects/immbrain.png",
    name: "IMMBRAIN",
    tag: "Finance Software",
    year: "2025",
    span: "hero",
    blurb: "A treasury and risk-modeling engine that builds portfolio, allocates capital, and surfaces problems early.",
    problem: "Trading is very fast paced. I needed a system that didn't need to rest. So I could rest instead!",
    approach: "Built an event-driven core with deterministic snapshots and a UI that treats numbers as first-class citizens. Risk scoring sits on top, not baked in.",
    impact: [
      "Identify weaknesses in Regimes and appropriately stake positions",
      "Grows portfolio to targeted amounts",
      "Audit-ready by design"
    ],
    stack: ["FastAPI", "Postgres", "Redis", "React", "TypeScript"],
    color: "#DC2626"
  },
  {
    id: "solar-iot",
    preview: "/assets/projects/solar-iot.svg",
    name: "Solar IoT Drip Irrigation",
    tag: "IoT / Hardware",
    year: "2026",
    span: "wide",
    blurb: "Solar-powered irrigation monitor built for Jamaican smallholder vegetable farmers. An ESP32 reads soil moisture, flow, tank level, and pump current. Six fault conditions send SMS alerts automatically through a 4G module.",
    problem: "Small vegetable farmers in rural Jamaica spend J$3,000 to J$5,000 per week on diesel to pump water, with no way to know if the pump is running dry, a drip line is clogged, or the tank is empty until a crop is already stressed or lost.",
    approach: "Designed and built a full water source to field pipeline for a 0.25 to 0.5 acre plot in St. Elizabeth parish. Two 225W solar panels and a 100Ah LiFePO4 battery bank power a 24V DC submersible pump drawing from a shallow well up into a 500L gravity-fed polyethylene tank on a 3m stand. An ESP32 microcontroller reads tank level via waterproof ultrasonic sensor, drip line flow, soil moisture at root zone, and pump current every five minutes. Threshold-based fault detection sends SMS alerts through a SIM7000G 4G module for dry-run, overcurrent, low battery, tank-full, no-flow, and soil-dry conditions. A Blynk dashboard gives smartphone users a secondary live view. The farmer keeps physical control of the irrigation valve. An SMS command option lets a farmer trigger a timed pump cycle remotely when they cannot reach the farm.",
    impact: [
      "Realistic installed cost J$580,000 to J$650,000 at Jamaica's market prices (approximately US$3,800 to US$4,200); the 24V 100Ah LiFePO4 battery alone runs J$150,000 to J$195,000 landed at a local supplier, which the report's original budget severely underestimated",
      "Net annual savings of J$211,000 against a diesel baseline of J$233,000 per year gives a payback of roughly 33 to 37 months, on a system designed to run 10 or more years",
      "Net savings over 10 years estimated at J$3.4 million as diesel prices continue rising faster than solar operating costs",
      "Drip delivery cuts water use 30 to 50 percent compared to sprinkler or furrow irrigation",
      "Sized for 0.25 to 0.5 acres; scales to 1 acre by doubling drip laterals, upgrading to a 500W pump, and adding a third 225W panel",
      "Six fault conditions detected automatically: dry-run, overcurrent, low battery, tank-full, no-flow, and soil-dry"
    ],
    stack: ["ESP32", "C++ / Arduino", "SIM7000G 4G", "Solar PV", "LiFePO4", "Blynk", "PlatformIO"],
    color: "#b45309"
  },
  {
    id: "surge-protector",
    preview: "/assets/projects/surge-protector.svg",
    name: "Surge Protector",
    tag: "Electrical Engineering",
    year: "2022",
    span: "tall",
    blurb: "A four-section AC surge protector built from components for ECE2004 lab coursework. It cuts power if input voltage drops below 90V or goes above 130V, then waits two minutes before reconnecting.",
    problem: "Voltage outside the 90V to 130V range damages equipment. Most protection circuits you can buy are passive. We had to build an active one from scratch that would detect a fault and respond to it.",
    approach: "The build was split across four sections. The power supply uses a full wave bridge rectifier and zener diodes to hold two regulated rails at 15V and 8V. Two TL072 op-amp comparators handle detection, one for the low end and one for the high end, each with its own LED to show which threshold was hit. When either trips, it switches a 2N3904 transistor that triggers a 555 timer in monostable mode. The timer drives a solid state relay that keeps the output off for two minutes. Timing was set using the 1.1RC formula with a 1000µF capacitor and a 100kΩ resistor. Total build cost was JMD $4,512.40.",
    impact: [
      "Circuit met the spec: cuts power below 90V and above 130V with a two minute reconnect delay",
      "All four sections were designed and assembled by the group from individual components",
      "Build cost of JMD $4,512.40 sits within the $4,000 to $12,000 range for commercial surge protectors of the same rating",
      "Potentiometer drift was the main reliability issue, trip points would shift if the board was disturbed during testing",
      "Recommended switching to fixed resistors in a future build and adding a varistor before the fuse for spike protection"
    ],
    stack: ["TL072 Op-Amp Comparator", "555 Timer (Monostable)", "Solid State Relay", "Bridge Rectifier", "Zener Regulation", "Circuit Design"],
    color: "#DC2626"
  },
  {
    id: "hvac-utech",
    preview: "/assets/projects/hvac.svg",
    name: "HVAC System for UTech Auditorium",
    tag: "Mechanical / Electrical",
    year: "2024",
    span: "sm",
    blurb: "A project management proposal for designing and installing an HVAC system in the UTech auditorium. The building had no climate control. This was the plan to fix it.",
    problem: "The UTech auditorium seats over a thousand people and had no HVAC system at all. Events were uncomfortable and the building was taking humidity damage. The project needed a real engineering solution with cost justification and a schedule that could actually be executed.",
    approach: "Worked in a team of four for MEE3010 under lecturer Errol Eccles. Put together a full project management proposal covering system design, procurement, installation and commissioning. The auditorium needed between 200 and 300 tons of cooling capacity. We calculated a total budget of $485,000 JMD split across HVAC units ($250K), ductwork ($45K), control systems ($18K) and energy-efficient components ($30K). The schedule covered 61 working days from December through February. We put together a full WBS, Gantt chart, risk register, stakeholder analysis, responsibility assignment matrix and cost baseline as part of the submission.",
    impact: [
      "Received top marks from academic evaluators and recognised for innovation and teamwork",
      "Proposed a 200 to 300 ton cooling system with a total project budget of $485,000 JMD",
      "Schedule of 61 working days across design, procurement, installation and commissioning phases",
      "Projected 20 percent reduction in energy costs compared to the building running without climate control",
      "Full project management documentation submitted including WBS, Gantt chart, risk register and responsibility assignment matrix"
    ],
    stack: ["HVAC Load Calculations", "Project Management", "WBS / Gantt", "Risk Analysis", "Cost Management", "Technical Documentation"],
    color: "#DC2626"
  }
];

export const experience = [
  {
    period: "2025",
    role: "Sales and Logistics Intern",
    org: "ATL Automotive Group",
    points: [
      "Worked with the vehicle transport team on safe and efficient car transportation.",
      "Handled dealership documentation, logistics coordination, and customer-facing sales support.",
      "Prepared and classified vehicle records including insurance paperwork."
    ]
  },
  {
    period: "2025",
    role: "Summer Intern",
    org: "First Global Bank Limited, New Kingston",
    points: [
      "Resolved day-to-day technical issues for staff across the office.",
      "Built spreadsheet systems that improved tracking of insurance-related records.",
      "Organised and filed documents to company procedures, improving record accuracy."
    ]
  },
  {
    period: "2020 to Present",
    role: "Engineering Student",
    org: "University of Technology Jamaica",
    points: [
      "Bachelor of Engineering in Electrical and Computer Engineering.",
      "Built IMMBRAIN and the solar IoT drip irrigation system alongside coursework.",
      "Top marks for HVAC auditorium project, recognised for innovation and teamwork."
    ]
  },
  {
    period: "2019 to 2022",
    role: "Lead Audio Technician",
    org: "Browns Town SDA Church",
    points: [
      "Led the audio room team for live streaming and weekly services.",
      "Board member providing research and quotes for equipment and services.",
      "Managed inventory and repaired musical equipment to keep services running."
    ]
  }
];

export const testimonials = [
  {
    quote: "Malicke approaches every problem with a methodical mindset. He digs into the details others skip and comes back with something that actually works.",
    name: "Lecturer, UTech Jamaica",
    role: "Engineering Faculty",
    initials: "UJ"
  },
  {
    quote: "He was reliable and took initiative. When something was broken he fixed it without being asked.",
    name: "Supervisor",
    role: "First Global Bank Limited",
    initials: "FG"
  },
  {
    quote: "The HVAC presentation was one of the strongest we evaluated that year. Thorough documentation, clear thinking, and a live demo that held up.",
    name: "Academic Evaluator",
    role: "University of Technology Jamaica",
    initials: "AE"
  }
];

export const blog = blogPosts;
